import {
  EnumDiscountType,
  EnumOrderPaymentMetadataStripeRegion,
  EnumProductType,
  InventoryByProductBookedDatesDocument,
  type OrderCreateMutationVariables,
  useDiscountByIdsQuery,
  useOrderCreateMutation,
} from "gql/generated";
import {
  useBookerContext,
  useCartContext,
  convertCartToProductPurchaseRequests,
} from "widgets/booker/contexts";
import { useEffect, useMemo, useRef, useState } from "react";
import { getDiscountNameString, getDiscountUnitInfoString, getPerTicketDiscountCount } from "shared/utils";
import { emitPurchaseCompleted } from "shared/utils/purchaseCompletedEvent";
import { PostHogCustomEvent } from "shared/hooks/usePostHog";
import { useWidgetTrack } from "widgets/shared/WidgetTrackingContext";
import { useTranslation } from "react-i18next";
import { useMutation, gql } from "@apollo/client";
import { useStripe } from "@stripe/react-stripe-js";
import { getDynamicFeeSurveyId } from "../Steps/components/DynamicFeeQuestionnaire";
import { getBusinessFieldOperatorIds } from "widgets/booker/contexts/CartContextProvider/cartContextTypes";
import { addressSearchValueToAddressInput } from "shared/components/organisms/SurveyQuestionInput/addressSearchResponse";
import { CheckoutErrorCode } from "@shared/checkout";
import {
  AFTER_SERVICE_PAYMENT_METHOD_ID,
  BOOKING_REQUEST_PAYMENT_METHOD_ID,
} from "widgets/booker/shared/utils";
import { useIsBookingRequest } from "widgets/booker/shared/hooks/useIsBookingRequest";
import { useIsInvoicedAfterService } from "widgets/booker/shared/hooks/useIsInvoicedAfterService";
import {
  ClientCheckoutError,
  getCheckoutBannerMessage,
  getCheckoutCodeFromError,
  stripeJsCheckoutError,
} from "./getCheckoutBannerMessage";

const RENEWABLE_FEE_INITIATE = gql`
  mutation RenewableFeeInitiate(
    $bookerId: MongoID!
    $productId: MongoID!
    $tierId: String
    $customer: RenewableFeeCustomerInput!
    $organizationId: MongoID!
    $paymentMethod: String
    $operatorId: MongoID
    $operatorIds: [MongoID!]
    $businessName: String
    $paymentRequestId: MongoID
    $broadcastId: MongoID
    $broadcastResourceId: String
    $promoCode: String
    $surveyResponse: SurveyResponseInput
    $taxExemption: RenewableFeeTaxExemptionInput
    $autoRenewOptIn: Boolean
    $addOns: [RenewableFeeAddOnInput!]
  ) {
    renewableFeeInitiate(
      bookerId: $bookerId
      productId: $productId
      tierId: $tierId
      customer: $customer
      organizationId: $organizationId
      paymentMethod: $paymentMethod
      operatorId: $operatorId
      operatorIds: $operatorIds
      businessName: $businessName
      paymentRequestId: $paymentRequestId
      broadcastId: $broadcastId
      broadcastResourceId: $broadcastResourceId
      promoCode: $promoCode
      surveyResponse: $surveyResponse
      taxExemption: $taxExemption
      autoRenewOptIn: $autoRenewOptIn
      addOns: $addOns
    ) {
      clientSecret
      orderId
      renewablePurchaseId
      paymentIntentId
    }
  }
`;

const RENEWABLE_FEE_CONFIRM = gql`
  mutation RenewableFeeConfirm(
    $renewablePurchaseId: MongoID!
    $paymentIntentId: String!
  ) {
    renewableFeeConfirm(
      renewablePurchaseId: $renewablePurchaseId
      paymentIntentId: $paymentIntentId
    ) {
      success
      renewablePurchaseId
      status
    }
  }
`;

/** Staff-entered orders are recorded without charging through Stripe. */
const isStaffRecordedPayment = (paymentMethodId: string | null | undefined) =>
  paymentMethodId === "offline" || paymentMethodId === "manual_order";

export const useCheckout = () => {
  const [status, setStatus] = useState({
    loading: false,
    error: false,
    errorMsg: "",
    orderComplete: false,
    /**
     * True from the moment a server-bound submit starts. It stays true through
     * any subsequent error to guard against a Stripe charge that has already
     * gone through but surfaced as a UI error (the user clicking Pay again
     * could otherwise create a duplicate PaymentIntent). The lock is released
     * automatically when the payment method changes (see useEffect below) so
     * the common "card declined → enter a new card → retry" path still works.
     */
    submissionLocked: false,
  });
  const isBookingRequest = useIsBookingRequest();
  const { isInvoicedAfterService } = useIsInvoicedAfterService();
  useEffect(() => {
    // A booking request isn't a purchase until the invoice is paid.
    if (status.orderComplete && !isBookingRequest) emitPurchaseCompleted();
  }, [status.orderComplete, isBookingRequest]);

  const [submittedPaymentMethodId, setSubmittedPaymentMethodId] = useState<
    string | null
  >(null);
  /**
   * A Stripe PaymentMethod can only ever be attached to one customer. If an
   * order submission fails after `completeOrderPayment` has already attached
   * the cached `pm_...` id to a Stripe customer (e.g. a later step throws),
   * blindly resubmitting the same cached id causes a confusing
   * "already been attached to a customer" error. Once any submission fails,
   * force the next attempt to mint a brand new PaymentMethod from the still-
   * mounted card element rather than reusing the possibly-consumed one.
   */
  const needsFreshPaymentMethodRef = useRef(false);
  const {
    customer,
    cart,
    stripeCardElement,
    cardPostalCode,
    paymentMethodId,
    guestNames,
    eventGuestDetails,
    discounts,
    promoCode,
    questionnaireResponses,
    grandTotal,
    selectedTierId,
    selectedOperatorId,
    businessField,
    customerAddress,
    effectiveTaxExempt,
    taxExemptionNumber,
    delivery,
    autoRenewOptIn,
    messageFromGuest,
  } = useCartContext();
  const businessFieldOperatorIds = getBusinessFieldOperatorIds(businessField);

  // Only claim the exemption once a valid number is entered (the priced
  // totals already reflect this; the backend re-validates the number too).
  const taxExemptionInput = effectiveTaxExempt
    ? { claimed: true, exemptionNumber: taxExemptionNumber }
    : undefined;

  useEffect(() => {
    if (
      submittedPaymentMethodId &&
      paymentMethodId !== submittedPaymentMethodId
    ) {
      setSubmittedPaymentMethodId(null);
      setStatus(prev => ({
        ...prev,
        submissionLocked: false,
        error: false,
        errorMsg: "",
      }));
    }
  }, [paymentMethodId, submittedPaymentMethodId]);
  const {
    booker,
    isEmbedded,
    language,
    product,
    broadcastId,
    broadcastResourceId,
    paymentRequestId,
    paymentRequestVersion,
    paymentRequestGrandTotal,
    paymentRequestAppliedDiscount,
  } = useBookerContext();
  const trackPostHogEvent = useWidgetTrack();
  const { t } = useTranslation("booker", { keyPrefix: "CHECKOUT" });

  const stripe = useStripe();

  const reportCheckoutFailure = (
    err: unknown,
    paymentKind: string,
  ) => {
    const checkoutCode =
      getCheckoutCodeFromError(err) ?? CheckoutErrorCode.Unknown;
    trackPostHogEvent(PostHogCustomEvent.PurchaseFailed, {
      bookerId: booker?._id,
      checkoutCode,
      paymentMethodKind: paymentKind,
    });
    return getCheckoutBannerMessage(err, t);
  };

  const failCheckout = (err: unknown, paymentKind: string) => {
    const errorMsg = reportCheckoutFailure(err, paymentKind);
    setStatus(prev => ({
      ...prev,
      loading: false,
      error: true,
      errorMsg,
    }));
  };

  const [createOrder] = useOrderCreateMutation({
    refetchQueries: [InventoryByProductBookedDatesDocument],
  });

  const [renewableFeeInitiate] = useMutation(RENEWABLE_FEE_INITIATE);
  const [renewableFeeConfirm] = useMutation(RENEWABLE_FEE_CONFIRM);

  const payingFrozenInvoice = paymentRequestGrandTotal != null;
  const dynamicFeeProduct = product as unknown as Parameters<
    typeof getDynamicFeeSurveyId
  >[0];
  const frozenDiscountId = paymentRequestAppliedDiscount?.discountId;

  const { data: frozenDiscountData } = useDiscountByIdsQuery({
    variables: { ids: frozenDiscountId ? [frozenDiscountId] : [] },
    skip: !payingFrozenInvoice || !frozenDiscountId,
  });

  const { data: discountsData } = useDiscountByIdsQuery({
    variables: {
      ids: discounts.map(d => d.discountId),
    },
    skip: payingFrozenInvoice,
  });
  const effectiveGrandTotal = paymentRequestGrandTotal ?? grandTotal;

  const bookerStripeRegion = booker?.paymentMetadata?.stripeRegion;
  const isCaStripe =
    bookerStripeRegion === EnumOrderPaymentMetadataStripeRegion.Ca;

  /**
   * Billing details attached to card PaymentMethods. The postal code is only
   * present when the split card form is in use (the combined CardElement
   * collects it inside Stripe's iframe and attaches it itself).
   */
  const cardBillingDetails = () => {
    const postalCode = cardPostalCode.trim();
    return {
      name: `${customer.firstName} ${customer.lastName}`.trim(),
      email: customer.email,
      phone: customer.phone || undefined,
      ...(postalCode ? { address: { postal_code: postalCode } } : {}),
    };
  };

  async function confirmDirectDebit(clientSecret: string): Promise<{
    error?: { message?: string };
    paymentIntent?: { status: string };
  }> {
    if (!stripe) {
      return {
        error: new ClientCheckoutError(CheckoutErrorCode.StripeUnavailable),
      };
    }

    if (isCaStripe) {
      const result = await stripe.confirmAcssDebitPayment(clientSecret, {
        payment_method: {
          billing_details: {
            name: `${customer.firstName} ${customer.lastName}`.trim(),
            email: customer.email,
          },
        },
      });
      return {
        error: result.error ? { message: result.error.message } : undefined,
        paymentIntent: result.paymentIntent
          ? { status: result.paymentIntent.status }
          : undefined,
      };
    }

    const collectResult = await stripe.collectBankAccountForPayment({
      clientSecret,
      params: {
        payment_method_type: "us_bank_account",
        payment_method_data: {
          billing_details: {
            name: `${customer.firstName} ${customer.lastName}`.trim(),
            email: customer.email,
          },
        },
      },
    });

    if (collectResult.error) {
      return {
        error: { message: collectResult.error.message },
      };
    }

    if (collectResult.paymentIntent?.status === "requires_confirmation") {
      const confirmResult = await stripe.confirmUsBankAccountPayment(clientSecret);
      return {
        error: confirmResult.error ? { message: confirmResult.error.message } : undefined,
        paymentIntent: confirmResult.paymentIntent
          ? { status: confirmResult.paymentIntent.status }
          : undefined,
      };
    }

    return {
      paymentIntent: collectResult.paymentIntent
        ? { status: collectResult.paymentIntent.status }
        : undefined,
    };
  }

  async function handleRenewableFeeCheckout() {
    if (status.submissionLocked || status.loading) return;

    const isFreeOrder = effectiveGrandTotal === 0;
    const isDirectDebit = paymentMethodId === "direct_debit";
    const isBankTransfer =
      paymentMethodId === "bank_transfer" ||
      paymentMethodId === "offline_payment";
    const isAlternativePayment = isDirectDebit || isBankTransfer;

    const paymentKind = isDirectDebit
      ? "direct_debit"
      : isBankTransfer
        ? paymentMethodId
        : isFreeOrder
          ? "free"
          : "card";

    if (
      !isFreeOrder &&
      !isStaffRecordedPayment(paymentMethodId) &&
      !booker?.paymentMetadata?.connectedAccountId
    ) {
      failCheckout(
        new ClientCheckoutError(CheckoutErrorCode.PaymentAccountMissing),
        paymentKind,
      );
      return;
    }

    if (
      !isFreeOrder &&
      !isAlternativePayment &&
      (!stripe || !stripeCardElement)
    ) {
      failCheckout(
        new ClientCheckoutError(CheckoutErrorCode.StripeUnavailable),
        paymentKind,
      );
      return;
    }

    if (!isFreeOrder && isDirectDebit && !stripe) {
      failCheckout(
        new ClientCheckoutError(CheckoutErrorCode.StripeUnavailable),
        paymentKind,
      );
      return;
    }

    // Lock further submissions until either: (a) we land on the success page,
    // or (b) the user updates their payment method (handled by the effect
    // above). This prevents a duplicate Stripe charge if any post-charge
    // backend step throws and we re-enable the Pay button.
    setSubmittedPaymentMethodId(paymentMethodId);
    setStatus(prev => ({
      ...prev,
      loading: true,
      submissionLocked: true,
      error: false,
      errorMsg: "",
    }));

    try {
      const { data } = await renewableFeeInitiate({
        variables: {
          bookerId: booker?._id,
          productId: product?._id,
          tierId: selectedTierId || undefined,
          customer: {
            email: customer.email,
            firstName: customer.firstName,
            lastName: customer.lastName,
            phone: customer.phone || undefined,
            ...(customerAddress
              ? { address: addressSearchValueToAddressInput(customerAddress) }
              : {}),
          },
          organizationId: booker?.storefront?.organization ?? booker?.storefront,
          paymentMethod: isAlternativePayment ? paymentMethodId : "card",
          operatorId:
            businessField?.operatorEntityId ||
            selectedOperatorId ||
            undefined,
          operatorIds:
            businessFieldOperatorIds.length > 1
              ? businessFieldOperatorIds
              : undefined,
          businessName: businessField?.businessName?.trim() || undefined,
          paymentRequestId: paymentRequestId || undefined,
          broadcastId: broadcastId || undefined,
          broadcastResourceId: broadcastResourceId || undefined,
          promoCode: promoCode.length > 0 ? promoCode : undefined,
          surveyResponse:
            getDynamicFeeSurveyId(dynamicFeeProduct)
              ? {
                  survey: getDynamicFeeSurveyId(dynamicFeeProduct),
                  responses: questionnaireResponses,
                }
              : undefined,
          taxExemption: taxExemptionInput,
          autoRenewOptIn: !isAlternativePayment && autoRenewOptIn,
          addOns:
            paymentRequestId || !booker?.renewableFeeAddOnsEnabled
              ? undefined
              : cart.slice(1).map(item => ({
                  productId: item.productId,
                  quantity: item.quantity,
                  customFieldResponses: item.customFieldResponses,
                  ...(item.businessScopes?.length
                    ? {
                        businessScopes: item.businessScopes.map(scope => ({
                          operatorId: scope.operatorId,
                          customFieldResponses: scope.customFieldResponses,
                        })),
                      }
                    : {}),
                })),
        },
      });

      const initiateResult = data?.renewableFeeInitiate;
      if (!initiateResult) {
        throw new ClientCheckoutError(CheckoutErrorCode.Unknown);
      }

      const { clientSecret, renewablePurchaseId, paymentIntentId } =
        initiateResult;

      if (paymentIntentId === "free_order" || effectiveGrandTotal === 0) {
        setStatus(prev => ({ ...prev, orderComplete: true, loading: false }));
        if (isEmbedded) {
          trackPostHogEvent(PostHogCustomEvent.PurchaseCompleted, {
            bookerId: booker?._id,
          });
        }
        return;
      }

      if (isDirectDebit && clientSecret && stripe) {
        const { error: debitError, paymentIntent } =
          await confirmDirectDebit(clientSecret);

        if (debitError) {
          failCheckout(
            debitError instanceof ClientCheckoutError
              ? debitError
              : stripeJsCheckoutError(debitError.message),
            paymentKind,
          );
          return;
        }

        if (
          paymentIntent?.status !== "succeeded" &&
          paymentIntent?.status !== "processing" &&
          paymentIntent?.status !== "requires_action"
        ) {
          failCheckout(
            new ClientCheckoutError(CheckoutErrorCode.ChargeFailed),
            paymentKind,
          );
          return;
        }

        await renewableFeeConfirm({
          variables: { renewablePurchaseId, paymentIntentId },
        });

        setStatus(prev => ({ ...prev, orderComplete: true, loading: false }));
        if (isEmbedded) {
          trackPostHogEvent(PostHogCustomEvent.PurchaseCompleted, {
            bookerId: booker?._id,
          });
        }
        return;
      }

      if (isBankTransfer) {
        await renewableFeeConfirm({
          variables: { renewablePurchaseId, paymentIntentId },
        });

        setStatus(prev => ({ ...prev, orderComplete: true, loading: false }));
        if (isEmbedded) {
          trackPostHogEvent(PostHogCustomEvent.PurchaseCompleted, {
            bookerId: booker?._id,
          });
        }
        return;
      }

      if (!clientSecret || !stripe || !stripeCardElement) {
        failCheckout(
          new ClientCheckoutError(CheckoutErrorCode.StripeUnavailable),
          paymentKind,
        );
        return;
      }

      const { error: confirmError, paymentIntent } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: stripeCardElement,
            billing_details: cardBillingDetails(),
          },
        });

      if (confirmError) {
        failCheckout(stripeJsCheckoutError(confirmError.message), paymentKind);
        return;
      }

      if (
        paymentIntent?.status === "succeeded" ||
        paymentIntent?.status === "processing"
      ) {
        await renewableFeeConfirm({
          variables: { renewablePurchaseId, paymentIntentId },
        });

        setStatus(prev => ({ ...prev, orderComplete: true, loading: false }));
        if (isEmbedded) {
          trackPostHogEvent(PostHogCustomEvent.PurchaseCompleted, {
            bookerId: booker?._id,
          });
        }
      } else {
        failCheckout(
          new ClientCheckoutError(CheckoutErrorCode.ChargeFailed),
          paymentKind,
        );
      }
    } catch (err) {
      failCheckout(err, paymentKind);
    }
  }

  async function handleCheckoutSubmit() {
    if (status.submissionLocked || status.loading) return;

    if (product?.type === EnumProductType.RenewableFee) {
      return handleRenewableFeeCheckout();
    }

    const isFreeOrder = effectiveGrandTotal === 0;

    const skipsPayment = isBookingRequest || isInvoicedAfterService;
    const isDirectDebitOrder =
      !skipsPayment && paymentMethodId === "direct_debit";
    const isBankTransferOrder =
      !skipsPayment &&
      (paymentMethodId === "bank_transfer" ||
        paymentMethodId === "offline_payment");
    const isCardOrder =
      !skipsPayment &&
      !isFreeOrder &&
      !isDirectDebitOrder &&
      !isBankTransferOrder;

    const paymentKind = isBookingRequest
      ? BOOKING_REQUEST_PAYMENT_METHOD_ID
      : isInvoicedAfterService
        ? AFTER_SERVICE_PAYMENT_METHOD_ID
        : isDirectDebitOrder
        ? "direct_debit"
        : isBankTransferOrder
          ? paymentMethodId
          : isFreeOrder
            ? "free"
            : "card";

    if (
      !isFreeOrder &&
      !isStaffRecordedPayment(paymentMethodId) &&
      !booker?.paymentMetadata?.connectedAccountId
    ) {
      failCheckout(
        new ClientCheckoutError(CheckoutErrorCode.PaymentAccountMissing),
        paymentKind,
      );
      return;
    }

    // Card orders require a mounted Stripe card element. The PaymentMethod
    // id may not yet be populated (see below), so we only hard-fail when the
    // card element itself is missing.
    if (isCardOrder && !stripeCardElement) {
      failCheckout(
        new ClientCheckoutError(CheckoutErrorCode.StripeUnavailable),
        paymentKind,
      );
      return;
    }

    // Lock further submissions until success or a payment-method change.
    setSubmittedPaymentMethodId(paymentMethodId);
    setStatus(prev => ({
      ...prev,
      loading: true,
      submissionLocked: true,
      error: false,
      errorMsg: "",
    }));

    try {
      const dynamicFeeSurveyId = getDynamicFeeSurveyId(dynamicFeeProduct);

      // The card-form change handler pre-creates a Stripe PaymentMethod
      // (asynchronously) the moment the card becomes complete. If the user
      // clicks Pay before that round-trip finishes, `paymentMethodId` is
      // still empty even though the card element is mounted. Create the
      // PaymentMethod inline here so the order gets a valid `pm_…` id
      // instead of failing with a generic Stripe error on the first click.
      let resolvedPaymentMethodId = paymentMethodId;
      if (
        isCardOrder &&
        stripeCardElement &&
        (!resolvedPaymentMethodId ||
          !resolvedPaymentMethodId.startsWith("pm_") ||
          needsFreshPaymentMethodRef.current)
      ) {
        if (!stripe) {
          throw new ClientCheckoutError(CheckoutErrorCode.StripeUnavailable);
        }
        const { error: pmError, paymentMethod } =
          await stripe.createPaymentMethod({
            type: "card",
            card: stripeCardElement,
            billing_details: cardBillingDetails(),
          });
        if (pmError || !paymentMethod) {
          throw new ClientCheckoutError(CheckoutErrorCode.CardInvalid);
        }
        resolvedPaymentMethodId = paymentMethod.id;
        needsFreshPaymentMethodRef.current = false;
      }

      const { data } = await createOrder({
        variables: {
          customer: {
            email: customer.email,
            phone: customer.phone,
            firstName: customer.firstName,
            lastName: customer.lastName,
            ...(customerAddress
              ? { address: addressSearchValueToAddressInput(customerAddress) }
              : {}),
          },
          guests:
            eventGuestDetails.length > 0
              ? eventGuestDetails
                  .filter(g => g.name.trim())
                  .map(g => ({
                    name: g.name,
                    ...(g.email.trim() ? { email: g.email } : {}),
                  }))
              : guestNames
                  .filter(v => v && v.length > 0)
                  .map(v => ({ name: v })),
          paymentMethodId: isBookingRequest
            ? BOOKING_REQUEST_PAYMENT_METHOD_ID
            : isInvoicedAfterService
              ? AFTER_SERVICE_PAYMENT_METHOD_ID
              : isFreeOrder
              ? "free_order"
              : resolvedPaymentMethodId,
          messageFromGuest: isBookingRequest
            ? messageFromGuest.trim() || undefined
            : undefined,
          productPurchaseRequests: convertCartToProductPurchaseRequests(
            cart,
            product?.pricing,
            selectedTierId,
          ),
          source: {
            booker: booker?._id,
            storefront: booker?.storefront,
            broadcastId: broadcastId || undefined,
            broadcastResourceId: broadcastResourceId || undefined,
            operatorId:
              businessField?.operatorEntityId ||
              selectedOperatorId ||
              undefined,
            operatorIds:
              booker?.perBusinessAnswersEnabled &&
              businessFieldOperatorIds.length > 1
                ? businessFieldOperatorIds
                : undefined,
            businessName: businessField?.businessName?.trim() || undefined,
            paymentRequestId: paymentRequestId || undefined,
            paymentRequestVersion: paymentRequestVersion ?? undefined,
          } as OrderCreateMutationVariables["source"],
          promoCode: promoCode.length > 0 ? promoCode : undefined,
          surveyResponse: booker?.survey
            ? { survey: booker.survey._id, responses: questionnaireResponses }
            : dynamicFeeSurveyId
              ? { survey: dynamicFeeSurveyId, responses: questionnaireResponses }
            : undefined,
          taxExemption: taxExemptionInput,
          delivery: delivery ?? undefined,
        },
      });

      const clientSecret = data?.orderCreate?.clientSecret;

      if (clientSecret && stripe && isDirectDebitOrder) {
        const { error, paymentIntent } =
          await confirmDirectDebit(clientSecret);

        if (error) {
          failCheckout(
            error instanceof ClientCheckoutError
              ? error
              : stripeJsCheckoutError(error.message),
            paymentKind,
          );
          return;
        }

        if (
          paymentIntent?.status === "processing" ||
          paymentIntent?.status === "succeeded" ||
          paymentIntent?.status === "requires_action"
        ) {
          setStatus(prev => ({ ...prev, orderComplete: true }));
          if (isEmbedded) {
            trackPostHogEvent(PostHogCustomEvent.PurchaseCompleted, {
              orderId: data?.orderCreate?.record?._id,
              bookerId: booker?._id,
            });
          }
        } else {
          failCheckout(
            new ClientCheckoutError(CheckoutErrorCode.ChargeFailed),
            paymentKind,
          );
        }
        return;
      }

      if (data?.orderCreate?.record?._id) {
        setStatus(prev => ({ ...prev, orderComplete: true }));

        if (isEmbedded && !isBookingRequest) {
          trackPostHogEvent(PostHogCustomEvent.PurchaseCompleted, {
            orderId: data.orderCreate.record._id,
            bookerId: booker?._id,
          });
        }
      }
    } catch (err) {
      if (isCardOrder) {
        needsFreshPaymentMethodRef.current = true;
      }
      failCheckout(err, paymentKind);
    } finally {
      setStatus(prev => ({ ...prev, loading: false }));
    }
  }

  const isRenewableFeeProduct = product?.type === EnumProductType.RenewableFee;

  const discountDisplayData = useMemo(() => {
    if (
      payingFrozenInvoice &&
      paymentRequestAppliedDiscount?.discountAmount &&
      paymentRequestAppliedDiscount.discountAmount > 0
    ) {
      const frozenDoc = frozenDiscountData?.discountByIds?.[0];
      const applied = paymentRequestAppliedDiscount;
      const total = applied.discountAmount;
      const unit = applied.unit ?? frozenDoc?.unit;
      const amount = applied.amount ?? frozenDoc?.amount ?? 0;
      const perTicket = !!frozenDoc?.perTicket;
      const labelName =
        applied.name ??
        (frozenDoc?.name
          ? getDiscountNameString(
              frozenDoc.name,
              frozenDoc.type,
              frozenDoc.promoCode,
              language,
            )
          : "Discount");
      const promoLabel = applied.promoCode ?? frozenDoc?.promoCode;
      return [
        {
          label:
            (promoLabel?.length ? promoLabel : labelName) +
            " | " +
            getDiscountUnitInfoString(unit, amount, t("OFF"), {
              perTicket,
              perTicketTranslation: t("OFF_PER_TICKET"),
              flatRateTranslation: t("FLAT_RATE"),
              ticketCount: getPerTicketDiscountCount(
                { unit, amount, perTicket },
                total,
              ),
            }) +
            (isRenewableFeeProduct
              ? " | " +
                (frozenDoc?.isOngoing
                  ? t("DISCOUNT_RENEWS_ANNUALLY")
                  : t("DISCOUNT_FIRST_YEAR_ONLY"))
              : ""),
          total,
          type: frozenDoc?.type ?? EnumDiscountType.AutoApplied,
          isMainProductOnly: !!frozenDoc?.excludeUpsells,
        },
      ];
    }

    return (
      discountsData?.discountByIds.map(discount => {
        const cartDiscountTotal =
          discounts.find(d => d.discountId === discount._id)?.total ?? 0;
        return {
          label:
            getDiscountNameString(
              discount.name,
              discount.type,
              discount.promoCode,
              language,
            ) +
            " | " +
            getDiscountUnitInfoString(discount.unit, discount.amount, t("OFF"), {
              perTicket: !!discount.perTicket,
              perTicketTranslation: t("OFF_PER_TICKET"),
              flatRateTranslation: t("FLAT_RATE"),
              ticketCount: getPerTicketDiscountCount(
                discount,
                cartDiscountTotal,
              ),
            }) +
            (isRenewableFeeProduct
              ? " | " +
                (discount.isOngoing
                  ? t("DISCOUNT_RENEWS_ANNUALLY")
                  : t("DISCOUNT_FIRST_YEAR_ONLY"))
              : ""),
          total: cartDiscountTotal,
          type: discount.type,
          isMainProductOnly: !!discount.excludeUpsells,
        };
      }) ?? []
    );
  }, [
    payingFrozenInvoice,
    paymentRequestAppliedDiscount,
    frozenDiscountData?.discountByIds,
    discountsData?.discountByIds,
    discounts,
    language,
    t,
    isRenewableFeeProduct,
  ]);

  return {
    onCheckoutSubmit: handleCheckoutSubmit,
    ...status,
    discountDisplayData,
    setStatus,
  };
};
